import { Flex, Tooltip, Typography } from 'antd';
import { t } from 'i18next';
import { useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { useAntTheme } from '../../hooks/useAntTheme';
import {
  MediaAnalyticsEvents,
  type MediaProjectInstanceResponse,
  MessageOrigin,
} from '../../types';
import { HoverableCard } from '../HoverableCard';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useChatSessionId } from '../../hooks/useChatSessionId';
import styled from 'styled-components';
import { sortBy } from 'lodash';
import { radius } from '@libertify/ui';

export const MessageSources = ({
  sources = [],
  displaySource,
  mainDocumentId,
  publicationDocuments,
}: {
  sources: MessageOrigin[] | undefined;
  displaySource: (source: MessageOrigin) => void;
  mainDocumentId?: string;
  publicationDocuments?: MediaProjectInstanceResponse['documents'];
}) => {
  const sorted = sortBy(sources, 'zone.position.page_number');
  return (
    <StyledWrapper>
      {sorted?.map((source, index) => (
        <MessageSource
          index={index}
          key={`${source.document_id}-${index}`}
          source={source}
          handleDisplaySource={displaySource}
          mainDocumentId={mainDocumentId}
          publicationDocuments={publicationDocuments}
        />
      ))}
    </StyledWrapper>
  );
};

const StyledWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 10px;
  margin: 10px;
`;

const SourceWrapper = ({
  isSourceFromPrimaryDocument,
  children,
  documentName,
}: {
  isSourceFromPrimaryDocument: boolean;
  children: React.ReactNode;
  documentName?: string;
}) => {
  if (isSourceFromPrimaryDocument) {
    return <>{children}</>;
  }
  return (
    <Tooltip
      title={t(`thisSourceIsFrom`, { documentName: documentName ?? '' })}
    >
      {children}
    </Tooltip>
  );
};

const MessageSource = ({
  source,
  handleDisplaySource,
  mainDocumentId,
  index,
  publicationDocuments,
}: {
  source: MessageOrigin;
  handleDisplaySource: (source: MessageOrigin) => void;
  mainDocumentId?: string;
  index: number;
  publicationDocuments?: MediaProjectInstanceResponse['documents'];
}) => {
  const [clicked, setClicked] = useState(false);
  const { theme } = useAntTheme();
  const isSourceFromPrimaryDocument = source.document_id === mainDocumentId;
  const documentName = useMemo(
    () =>
      Object.values(publicationDocuments || {}).find(
        (x) => x.id === source.document_id,
      )?.name,
    [publicationDocuments, source.document_id],
  );
  const pageNumber = source.zone?.position?.page_number;
  const text = source.zone?.quote ?? '';
  const { trackEvent } = useAnalytics();
  const getChatSessionId = useChatSessionId();

  return (
    <HoverableCard
      hoverable
      active={clicked}
      style={{
        borderRadius: radius.box,
        background: theme.colorBgLayout,
      }}
      styles={{
        body: {
          whiteSpace: 'pre-wrap',
          position: 'relative',
          height: '100%',
          cursor: 'pointer',
        },
      }}
      size="small"
      onClick={() => {
        if (!isSourceFromPrimaryDocument) return;
        setClicked(true);
        handleDisplaySource(source);
      }}
    >
      <SourceWrapper
        documentName={documentName}
        isSourceFromPrimaryDocument={isSourceFromPrimaryDocument}
      >
        <Flex
          onClick={() => {
            trackEvent(MediaAnalyticsEvents.ChatSourceClick, {
              source_index: index,
              chat_session_id: getChatSessionId(),
            });
          }}
          vertical
          justify="space-between"
          style={{ height: '100%' }}
        >
          <Typography.Paragraph
            ellipsis={{ rows: 5 }}
            style={{ fontSize: 13, lineHeight: '1.2em', marginBottom: 3 }}
          >
            {text}
          </Typography.Paragraph>
          <Typography.Paragraph
            strong
            style={{
              fontSize: 13,
              margin: 0,
              color: clicked ? theme.colorPrimary : undefined,
            }}
            type="secondary"
          >
            {isSourceFromPrimaryDocument ? (
              <Trans key="pageNumber" pageNumber={pageNumber}>
                {t('Page')} {{ pageNumber }}
              </Trans>
            ) : (
              <Typography.Text italic>{t('privateSource')}</Typography.Text>
            )}
          </Typography.Paragraph>
        </Flex>
      </SourceWrapper>
    </HoverableCard>
  );
};
